import { listOrderLineHookVideos } from "@/features/marketplace/lib/order-line-hooks";
import { isUsableScriptText } from "@/features/opportunities/lib/script-display-title";

import type {
  OrderScriptProduction,
  OrderScriptRow,
  OrderScriptScene,
  OrderScriptStoryboardImage,
  OrderScriptStoryboardPanel,
} from "../../types/marketplace.types";

export interface PartnerScriptMetaField {
  key: "duration" | "presenter" | "product" | "background";
  value: string;
}

export interface PartnerScriptProductionView {
  adDescription: string | null;
  concept: string | null;
  fullScript: string | null;
  scenes: OrderScriptScene[];
  storyboardImages: Array<OrderScriptStoryboardImage & { imageUrl: string }>;
  storyboardPanels: OrderScriptStoryboardPanel[];
  meta: PartnerScriptMetaField[];
  rationale: string | null;
}

function usable(value: string | null | undefined): string | null {
  return isUsableScriptText(value) ? value!.trim() : null;
}

export function describePartnerScriptProduction(
  row: Pick<OrderScriptRow, "production">,
): PartnerScriptProductionView {
  const production: OrderScriptProduction | null = row.production ?? null;
  if (!production) {
    return {
      adDescription: null,
      concept: null,
      fullScript: null,
      scenes: [],
      storyboardImages: [],
      storyboardPanels: [],
      meta: [],
      rationale: null,
    };
  }

  const meta = (
    [
      ["duration", production.duration],
      ["presenter", production.presenter],
      ["product", production.product],
      ["background", production.background],
    ] as const
  ).flatMap(([key, value]) => {
    const next = usable(value);
    return next ? [{ key, value: next }] : [];
  });

  const scenes = Array.isArray(production.scenes) ? production.scenes : [];
  const images = Array.isArray(production.storyboardImages)
    ? production.storyboardImages
    : [];

  return {
    adDescription: usable(production.adDescription),
    concept: usable(production.concept),
    fullScript: usable(production.script),
    scenes,
    storyboardImages: images.filter(
      (image): image is OrderScriptStoryboardImage & { imageUrl: string } =>
        typeof image.imageUrl === "string" && image.imageUrl.length > 0,
    ),
    storyboardPanels: Array.isArray(production.storyboardPanels)
      ? production.storyboardPanels
      : [],
    meta,
    rationale: usable(production.rationale),
  };
}

export interface PartnerHookSlot {
  /** Null only when the primary's key is unknown. The primary is always submitted without a key. */
  hookKey: string | null;
  index: number;
  isPrimary: boolean;
  voiceover: string | null;
  onScreenText: string | null;
  isDelivered: boolean;
  scriptReviewId: string | null;
}

/** One slot per hook the partner shoots, primary first. Empty on one-hook lines. */
export function describePartnerHookSlots(
  row: Pick<OrderScriptRow, "production"> &
    Partial<
      Pick<OrderScriptRow, "hookDeliveries" | "preflightUploadId" | "preflightUrl" | "scriptReviewId">
    >,
): PartnerHookSlot[] {
  const liveHooks = row.production?.hooks ?? [];
  if (liveHooks.length > 1) {
    return liveHooks.map((hook, offset) => ({
      hookKey: hook.hookKey,
      index: offset + 1,
      isPrimary: hook.isPrimary,
      voiceover: usable(hook.voiceover),
      onScreenText: usable(hook.onScreenText),
      isDelivered: Boolean(hook.preflightUploadId),
      scriptReviewId: hook.scriptReviewId,
    }));
  }
  return listOrderLineHookVideos({
    hookDeliveries: row.hookDeliveries,
    preflightUploadId: row.preflightUploadId ?? null,
    preflightUrl: row.preflightUrl ?? null,
    scriptReviewId: row.scriptReviewId ?? null,
  }).map((video) => ({
    hookKey: video.hookKey,
    index: video.index,
    isPrimary: video.isPrimary,
    voiceover: null,
    onScreenText: null,
    isDelivered: video.hasUpload,
    scriptReviewId: video.scriptReviewId,
  }));
}

export function hasPartnerScriptProduction(view: PartnerScriptProductionView): boolean {
  return (
    view.adDescription != null ||
    view.concept != null ||
    view.fullScript != null ||
    view.scenes.length > 0 ||
    view.storyboardImages.length > 0 ||
    view.storyboardPanels.some(
      (panel) =>
        typeof panel.imageUrl === "string" && panel.imageUrl.length > 0,
    ) ||
    view.meta.length > 0 ||
    view.rationale != null
  );
}
